import type {
  ApiV2Profile,
  ApiV2UserProfile,
} from '@dce-front/hodor-types/api/v2/me/profiles/definitions';
import {
  addQueriesParam,
  fillTemplate,
  PROFILE,
} from '@dce-front/onewebapp-utils';
import { requestPage, type HodorSdkConfig } from '@dce-front/sdk-hodor';
import type { AppKey } from '../../../../../config/application/types';
import { getPublicConfig } from '../../../../helpers/config/config-helper';
import logger from '../../../../helpers/logger/logger-helper';
import { rewriteObjectUrls } from '../../../../helpers/proxyUrls/proxyUrls';
import {
  getAllProfiles,
  removeAllProfiles,
  setCurrentProfile,
  setProfiles,
  setProfilesUpdateDate,
} from '../../../../services/profiles/profileStorageService';

/**
 * Get the url hodor service to get list profiles
 * @param appKey (see const `AppKey`)
 */
export const getListProfileUrl = (appKey: AppKey): string => {
  const {
    baseUrl,
    initParameters: { allowedProfiles },
    profileParameters: { profilesTemplateVersion },
    paths: { profileUrl },
  } = getPublicConfig().api.hodor;

  const url = `${fillTemplate(baseUrl, [['appKey', appKey]])}/${profileUrl}`;

  return addQueriesParam(url, [
    ['allowedProfiles', allowedProfiles],
    ['profilesTemplateVersion', profilesTemplateVersion],
  ]);
};

/**
 * Sort list profiles to have the `currentProfile` at the first position.
 * If you don't get the parameter `currentProfile`, you will have at first position the profile with the property currentProfile = true
 * @param profiles list profiles to sort
 * @param currentProfile currentProfile
 */
export const sortProfiles = (
  profiles: ApiV2Profile[],
  currentProfile?: ApiV2Profile,
): ApiV2Profile[] => {
  const defaultProfileIndex = profiles.findIndex(
    (profile) =>
      (currentProfile && currentProfile.profileId === profile.profileId) ||
      (!currentProfile && profile.currentProfile),
  );

  const sortedProfiles = [...profiles];

  if (defaultProfileIndex > -1) {
    sortedProfiles.splice(defaultProfileIndex, 1);

    profiles[defaultProfileIndex] &&
      sortedProfiles.unshift(profiles[defaultProfileIndex]);
  }

  return sortedProfiles;
};

/**
 * Return true if it's a kids profile
 * @param profile
 */
export const isKidsProfile = (profile?: ApiV2Profile) => {
  return !!profile?.isKidsProfile;
};

/**
 *  Promise to get profiles :
 * - from `localStorage` if data exist and `profilesUpdateDate` is up to date
 * - from hodor service if no data in `localStorage` or `profilesUpdateDate` is not up to date.
 *   And store result profiles to `localStorage` for the next time.
 * @param hodorSdkConfig
 * @param profilesUpdateDate
 * @param appKey
 */
export const getProfiles = async (
  hodorSdkConfig: HodorSdkConfig,
  profilesUpdateDate: string,
  appKey: AppKey,
) => {
  const {
    currentProfile,
    profiles: profilesLocalStorage,
    profilesUpdateDate: profilesUpdateDateStored,
  } = getAllProfiles();

  const storageLocalValid =
    profilesUpdateDate === profilesUpdateDateStored &&
    currentProfile &&
    profilesLocalStorage;

  // case storage local exist and profilesUpdateDate is ok, get from storage local
  if (storageLocalValid) {
    return rewriteObjectUrls(
      sortProfiles(profilesLocalStorage, currentProfile),
    );
  }

  // case storage local not exist or profilesUpdateDate is not ok, get from service and update storage local
  try {
    const { contents: newProfiles } = (await requestPage(hodorSdkConfig, {
      url: getListProfileUrl(appKey),
      parameters: [
        { id: 'accept-language', in: 'header' },
        { id: 'tokenPass', in: 'header' },
        { id: 'xx-profile-id', in: 'header' },
      ],
    })) as ApiV2UserProfile;

    const profileList = newProfiles.filter((info) => info.type === PROFILE);

    const sortedProfiles = sortProfiles(profileList);

    // update storage local
    setProfiles(profileList);
    if (sortedProfiles[0]) {
      setCurrentProfile(sortedProfiles[0]);
    }
    setProfilesUpdateDate(profilesUpdateDate);

    return rewriteObjectUrls(sortedProfiles);
  } catch (error) {
    removeAllProfiles();
    logger.error(
      `profilesHelper::getProfiles() - Error occurs during getProfiles from hodor ${error}`,
    );
  }
};
